// Die Heimflug-Sequenz: Sprung, Ankunft, Truemmer.
//
// **Sie ist reine Ausgabe.** Die Aera steht bereits im Spielstand, bevor der
// erste Stern sich streckt — gebucht hat der Zug, nicht diese Szene. Sie
// entscheidet nichts, sie bucht nichts, sie schreibt keinen Zustand (Gate
// FG-17). Wer sie ueberspringt, steht im selben Zustand wie danach.
//
// Das ist kein Schoenheitsprinzip, sondern das einzige, was traegt: eine
// Zwischensequenz, die etwas entscheidet, ist der kuerzeste Weg zu einem
// Spielstand, den nur sieht, wer sie nicht uebersprungen hat.
//
// Wie ein Dialog haelt sie die Simulation an. Eine weiterlaufende Welt hinter
// einer Sequenz verbraucht Energie, waehrend niemand zusieht — und beim
// Schliessen ist unerklaerlich, was passiert ist.
//
// Alles entsteht prozedural: Streifen, Blitz, Schwenk, Truemmersilhouetten.
// Kein Bild-Asset, kein Ton-Asset (Gates FG-10, FG-40).

import Phaser from 'phaser';

import { sfx } from '../audio/synth';
import { bus } from '../core/bus';
import { TAU, clamp } from '../core/math';
import { rngFrom } from '../core/rng';

/** Die Abschnitte der Sequenz, in Sekunden ab Beginn. */
const LADEN = 3.2;
const SPRUNG = LADEN + 1.1;
const ANKUNFT = SPRUNG + 2.0;
const SCHWENK = ANKUNFT + 6.5;

/** Wie viele Sterne sich strecken. Einmal gezogen, nicht je Bild. */
const STERNE = 220;
/** Wie viele Truemmerstuecke im Heimatsystem treiben. */
const TRUEMMER = 34;
/** Saat der Sequenz — sie sieht bei jedem gleich aus, wie das Feld auch. */
const SAAT = 0x48414d;

interface Stern {
  x: number;
  y: number;
  laenge: number;
  ton: number;
}

interface Stueck {
  x: number;
  y: number;
  r: number;
  winkel: number;
  ecken: number;
  ton: number;
}

/** Die Zeilen, die waehrend des Schwenks stehen. Deutsch, mit Umlauten. */
const ZEILEN: ReadonlyArray<readonly [number, string]> = [
  [ANKUNFT + 0.4, 'Sprung abgeschlossen. Heimatsystem erreicht.'],
  [ANKUNFT + 2.2, 'Keine Antwort auf allen Kanälen.'],
  [ANKUNFT + 4.0, 'Die Werften sind Trümmer. Die Kuppel ist offen.'],
  [ANKUNFT + 5.6, 'Du bist zu spät gekommen — aber du bist da.'],
];

export class JumpScene extends Phaser.Scene {
  static readonly KEY = 'jump';

  private gfx!: Phaser.GameObjects.Graphics;
  private label!: Phaser.GameObjects.Text;
  private hinweis!: Phaser.GameObjects.Text;

  /** Laufzeit der Sequenz. Sie wird in `update` fortgeschrieben, nie im Zeichnen. */
  private t = 0;
  private beendet = false;

  private readonly sterne: Stern[] = [];
  private readonly truemmer: Stueck[] = [];

  constructor() {
    super(JumpScene.KEY);
  }

  create(): void {
    const { width, height } = this.scale;

    this.gfx = this.add.graphics().setDepth(0).setScrollFactor(0);
    this.label = this.add.text(width / 2, height * 0.72, '', {
      fontFamily: '"Lucida Console", monospace', fontSize: '15px', color: '#cfe8ff',
      align: 'center', wordWrap: { width: Math.min(560, width - 60) },
    }).setOrigin(0.5).setDepth(2).setScrollFactor(0);
    this.hinweis = this.add.text(width / 2, height - 26, 'Leertaste oder Tippen überspringt', {
      fontFamily: '"Lucida Console", monospace', fontSize: '11px', color: '#6b7d8f',
    }).setOrigin(0.5).setDepth(2).setScrollFactor(0);

    this.buildStars();
    this.buildDebris();

    // Ueberspringen: Taste **und** Fingertipp. Ein Finger kennt kein Zeigen —
    // eine Bedienung, die nur ueber die Tastatur erreichbar ist, gibt es am
    // Handy nicht (`oberflaeche.md`).
    this.input.keyboard?.on('keydown', () => this.finish());
    this.input.on('pointerdown', () => this.finish());

    sfx.warp();
  }

  /** Sterne und Truemmer entstehen **einmal**, nicht je Bild (Gate FG-14). */
  private buildStars(): void {
    const rng = rngFrom(SAAT);
    const { width, height } = this.scale;
    for (let i = 0; i < STERNE; i++) {
      this.sterne.push({
        x: rng() * width,
        y: rng() * height,
        laenge: 0.35 + rng() * 0.65,
        ton: rng(),
      });
    }
  }

  private buildDebris(): void {
    const rng = rngFrom(SAAT ^ 0x5eed);
    const { width, height } = this.scale;
    for (let i = 0; i < TRUEMMER; i++) {
      this.truemmer.push({
        x: rng() * width * 1.6 - width * 0.3,
        y: height * (0.18 + rng() * 0.5),
        r: 6 + rng() * 46,
        winkel: rng() * TAU,
        ecken: 5 + Math.floor(rng() * 4),
        ton: rng(),
      });
    }
  }

  override update(_time: number, delta: number): void {
    // **Hier** laeuft die Uhr, nicht im Zeichenpfad (Gate FG-17).
    this.t += Math.min(0.05, delta / 1000);
    this.draw();
    if (this.t >= SCHWENK) this.finish();
  }

  /** Nur lesen und zeichnen. Diese Methode weist nichts zu. */
  private draw(): void {
    const g = this.gfx;
    const { width, height } = this.scale;
    g.clear();

    if (this.t < SPRUNG) this.drawCharge(g, width, height);
    else if (this.t < ANKUNFT) this.drawFlash(g, width, height);
    else this.drawHome(g, width, height);

    this.label.setText(this.lineAt());
  }

  /** Der Beschleuniger laedt: Sterne strecken sich, der Ring pulst. */
  private drawCharge(g: Phaser.GameObjects.Graphics, width: number, height: number): void {
    const f = clamp(this.t / LADEN, 0, 1);
    g.fillStyle(0x05070d, 1);
    g.fillRect(0, 0, width, height);

    const mx = width / 2;
    const my = height / 2;
    for (const stern of this.sterne) {
      const dx = stern.x - mx;
      const dy = stern.y - my;
      const zug = f * f * stern.laenge * 260;
      const dist = Math.hypot(dx, dy) || 1;
      g.lineStyle(1, 0x9fd8ff, 0.25 + stern.ton * 0.55);
      g.lineBetween(
        stern.x, stern.y,
        stern.x + (dx / dist) * zug, stern.y + (dy / dist) * zug,
      );
    }

    const puls = 0.55 + Math.sin(this.t * 9) * 0.25;
    g.lineStyle(3, 0x5fe6d8, puls * f);
    g.strokeCircle(mx, my, 60 + f * 90);
    g.lineStyle(1.5, 0xf0c437, puls * f * 0.8);
    g.strokeCircle(mx, my, 40 + f * 60);
  }

  /** Der Sprung reisst das Bild auf. */
  private drawFlash(g: Phaser.GameObjects.Graphics, width: number, height: number): void {
    const f = clamp((this.t - SPRUNG) / (ANKUNFT - SPRUNG), 0, 1);
    // Von Weiss ins Schwarze — die Stille danach ist der eigentliche Effekt.
    const hell = Math.round(255 * (1 - f) ** 2);
    g.fillStyle((hell << 16) | (hell << 8) | hell, 1);
    g.fillRect(0, 0, width, height);
  }

  /**
   * Das Heimatsystem: ein langsamer Schwenk ueber das, was uebrig ist.
   *
   * Die Truemmer sind Silhouetten, keine Objekte — sie stehen fuer die
   * Zerstoerung, nicht fuer abbaubares Gestein. Wer hier Brocken zeichnet,
   * verspricht ein Feld, das die Szene nicht hat.
   */
  private drawHome(g: Phaser.GameObjects.Graphics, width: number, height: number): void {
    const f = clamp((this.t - ANKUNFT) / (SCHWENK - ANKUNFT), 0, 1);
    g.fillStyle(0x0a0910, 1);
    g.fillRect(0, 0, width, height);

    // Ein kalter Rest von Sonne, tief am Rand.
    g.fillStyle(0x1a1420, 0.9);
    g.fillCircle(width * 0.78, height * 0.22, 120);
    g.fillStyle(0x2a1e2c, 0.7);
    g.fillCircle(width * 0.78, height * 0.22, 74);

    const schwenk = f * width * 0.35;
    for (const stueck of this.truemmer) {
      const x = stueck.x - schwenk * (0.4 + stueck.ton * 0.8);
      if (x < -80 || x > width + 80) continue;
      const ton = 0x14 + Math.round(stueck.ton * 0x2c);
      g.fillStyle((ton << 16) | (ton << 8) | (ton + 8), 1);
      g.beginPath();
      for (let i = 0; i <= stueck.ecken; i++) {
        const a = stueck.winkel + (i / stueck.ecken) * TAU;
        const r = stueck.r * (0.72 + ((i * 37) % 11) / 24);
        const px = x + Math.cos(a) * r;
        const py = stueck.y + Math.sin(a) * r * 0.7;
        if (i === 0) g.moveTo(px, py); else g.lineTo(px, py);
      }
      g.closePath();
      g.fillPath();
    }
  }

  /** Welche Zeile gerade steht — eine reine Frage an die Uhr. */
  private lineAt(): string {
    let text = '';
    for (const [ab, zeile] of ZEILEN) {
      if (this.t >= ab) text = zeile;
    }
    return text;
  }

  /**
   * Zurueck ins Spiel.
   *
   * **Idempotent**: Taste, Tipp und Ablauf treffen sich hier, und ein zweiter
   * Aufruf darf die Welt nicht ein zweites Mal freigeben.
   */
  private finish(): void {
    if (this.beendet) return;
    this.beendet = true;
    this.hinweis.setVisible(false);
    // **Ein** Ausgang fuer beide Wege — Ablauf und Ueberspringen. Wer die
    // Welt anhaelt, laesst sie hier wieder anlaufen; die Verdrahtung sitzt in
    // `main.ts`, denn `scenes` und `ui` kennen sich nicht (Gate FG-04).
    bus.emit('jump:done', undefined);
    this.scene.stop();
  }
}
